import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import ImageUploadButton from "../../components/admin/ImageUploadButton";

const inputCls = "w-full border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none transition-colors focus:border-accent";

const EMPTY_BUSINESS = {
  name_kana: "",
  name_en: "",
  corporate_number: "",
  invoice_number: "",
  postal_code: "",
  ceo_title: "代表取締役",
  ceo_name: "",
  website: "",
  established: "",
  capital: "",
  business_desc: "",
  licenses: "",
  logo_url: "",
  seal_url: "",
  notes: "",
};

const EMPTY_PROFILE = {
  contact: { tel: "", fax: "", email: "", hours: "", holiday: "", zip: "", address: "" },
  overview: [],
  history: [],
  business: EMPTY_BUSINESS,
};

const BUSINESS_FIELDS = [
  ["name_kana", "フリガナ"],
  ["name_en", "英文名"],
  ["corporate_number", "法人番号"],
  ["invoice_number", "適格請求書発行事業者登録番号"],
  ["postal_code", "郵便番号（帳票用）"],
  ["ceo_title", "代表者役職"],
  ["ceo_name", "代表者氏名"],
  ["website", "ホームページURL"],
  ["established", "設立年月日"],
  ["capital", "資本金"],
];

const CONTACT_FIELDS = [
  ["zip", "郵便番号"],
  ["address", "所在地"],
  ["tel", "TEL"],
  ["fax", "FAX"],
  ["email", "代表メール"],
  ["hours", "営業時間"],
  ["holiday", "定休日"],
];

export default function AdminCompany() {
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [changeLog, setChangeLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/admin/company")
      .then((r) => setProfile({ ...EMPTY_PROFILE, ...r.data, business: { ...EMPTY_BUSINESS, ...(r.data.business || {}) } }))
      .catch(() => toast.error("会社情報の取得に失敗しました"))
      .finally(() => setLoading(false));
    api.get("/admin/company/history").then((r) => setChangeLog(r.data)).catch(() => {});
  }, []);

  const setContact = (key) => (e) => setProfile((p) => ({ ...p, contact: { ...p.contact, [key]: e.target.value } }));

  const setBusiness = (key) => (e) => setProfile((p) => ({ ...p, business: { ...p.business, [key]: e.target.value } }));

  const setRow = (section, i, key, value) =>
    setProfile((p) => ({ ...p, [section]: p[section].map((r, j) => (j === i ? { ...r, [key]: value } : r)) }));

  const addRow = (section, blank) => setProfile((p) => ({ ...p, [section]: [...p[section], blank] }));

  const removeRow = (section, i) => setProfile((p) => ({ ...p, [section]: p[section].filter((_, j) => j !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/company", { contact: profile.contact, overview: profile.overview, history: profile.history, business: profile.business });
      toast.success("会社情報を保存しました。公開サイトに反映されます。");
      const h = await api.get("/admin/company/history");
      setChangeLog(h.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="font-mono text-sm tracking-[0.3em] text-gray-500">LOADING...</p>;
  }

  return (
    <div data-testid="admin-company-page" className="max-w-4xl">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-mincho text-2xl font-bold text-white">会社情報の編集</h1>
          <p className="mt-1 font-mono text-[10px] tracking-[0.3em] text-gray-500">COMPANY PROFILE — 会社概要ページに反映されます</p>
        </div>
        <button
          data-testid="company-save-button"
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 bg-accent px-5 py-2.5 font-display text-xs font-bold tracking-[0.15em] text-ink transition-colors hover:bg-accent-hover disabled:opacity-50"
        >
          <Save className="h-4 w-4" />
          {saving ? "保存中..." : "保存する"}
        </button>
      </div>

      <section className="mt-10 border border-line bg-panel p-6 md:p-8">
        <h2 className="font-mincho text-lg font-bold text-white">連絡先・基本情報</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {CONTACT_FIELDS.map(([key, label]) => (
            <label key={key} className="block">
              <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">{label}</span>
              <input
                data-testid={`company-contact-${key}`}
                value={profile.contact[key] || ""}
                onChange={setContact(key)}
                className={`mt-1.5 ${inputCls}`}
              />
            </label>
          ))}
        </div>
      </section>

      <section className="mt-8 border border-line bg-panel p-6 md:p-8">
        <h2 className="font-mincho text-lg font-bold text-white">法人・帳票情報（見積書・請求書に自動反映されます）</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {BUSINESS_FIELDS.map(([key, label]) => (
            <label key={key} className="block">
              <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">{label}</span>
              <input
                data-testid={`company-business-${key}`}
                value={profile.business[key] || ""}
                onChange={setBusiness(key)}
                className={`mt-1.5 ${inputCls}`}
              />
            </label>
          ))}
        </div>
        <div className="mt-4 space-y-4">
          <label className="block">
            <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">事業内容（帳票用）</span>
            <textarea data-testid="company-business-business_desc" rows={2} value={profile.business.business_desc} onChange={setBusiness("business_desc")} className={`mt-1.5 resize-y ${inputCls}`} />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">建設業許可・その他許認可</span>
            <textarea data-testid="company-business-licenses" rows={2} value={profile.business.licenses} onChange={setBusiness("licenses")} className={`mt-1.5 resize-y ${inputCls}`} />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">備考</span>
            <textarea data-testid="company-business-notes" rows={2} value={profile.business.notes} onChange={setBusiness("notes")} className={`mt-1.5 resize-y ${inputCls}`} />
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">会社ロゴ</span>
              <div className="mt-1.5 flex items-start gap-3">
                {profile.business.logo_url && <img src={profile.business.logo_url} alt="ロゴ" className="h-14 w-auto border border-line bg-white object-contain p-1" />}
                <ImageUploadButton testid="company-logo-upload" onUploaded={(url) => setProfile((p) => ({ ...p, business: { ...p.business, logo_url: url } }))} />
              </div>
            </div>
            <div>
              <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">会社印影（帳票用・非公開）</span>
              <div className="mt-1.5 flex items-start gap-3">
                {profile.business.seal_url && <img src={profile.business.seal_url} alt="印影" className="h-14 w-14 border border-line bg-white object-contain p-1" />}
                <ImageUploadButton testid="company-seal-upload" onUploaded={(url) => setProfile((p) => ({ ...p, business: { ...p.business, seal_url: url } }))} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mt-8 border border-line bg-panel p-6 md:p-8">
        <div className="flex items-center justify-between">
          <h2 className="font-mincho text-lg font-bold text-white">会社概要（項目と内容）</h2>
          <button
            data-testid="overview-add-button"
            onClick={() => addRow("overview", { label: "", value: "" })}
            className="flex items-center gap-2 border border-line px-4 py-2 font-display text-xs font-bold tracking-[0.15em] text-gray-300 transition-colors hover:border-accent hover:text-accent"
          >
            <Plus className="h-4 w-4" />
            行を追加
          </button>
        </div>
        <div className="mt-6 space-y-3">
          {profile.overview.map((row, i) => (
            <div key={i} data-testid={`overview-row-${i}`} className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <input
                data-testid={`overview-row-label-${i}`}
                value={row.label}
                onChange={(e) => setRow("overview", i, "label", e.target.value)}
                placeholder="項目（例：資本金）"
                className={`sm:w-48 ${inputCls}`}
              />
              <input
                data-testid={`overview-row-value-${i}`}
                value={row.value}
                onChange={(e) => setRow("overview", i, "value", e.target.value)}
                placeholder="内容"
                className={`flex-1 ${inputCls}`}
              />
              <button
                data-testid={`overview-row-delete-${i}`}
                onClick={() => removeRow("overview", i)}
                className="self-end border border-line p-2.5 text-gray-500 transition-colors hover:border-red-500 hover:text-red-400 sm:self-auto"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          {profile.overview.length === 0 && <p className="text-sm text-gray-500">項目がありません。「行を追加」から追加してください。</p>}
        </div>
      </section>

      <section className="mt-8 border border-line bg-panel p-6 md:p-8">
        <div className="flex items-center justify-between">
          <h2 className="font-mincho text-lg font-bold text-white">沿革</h2>
          <button
            data-testid="history-add-button"
            onClick={() => addRow("history", { year: "", text: "" })}
            className="flex items-center gap-2 border border-line px-4 py-2 font-display text-xs font-bold tracking-[0.15em] text-gray-300 transition-colors hover:border-accent hover:text-accent"
          >
            <Plus className="h-4 w-4" />
            年を追加
          </button>
        </div>
        <div className="mt-6 space-y-3">
          {profile.history.map((row, i) => (
            <div key={i} data-testid={`history-row-${i}`} className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <input
                data-testid={`history-row-year-${i}`}
                value={row.year}
                onChange={(e) => setRow("history", i, "year", e.target.value)}
                placeholder="年（例：2026）"
                className={`sm:w-28 ${inputCls}`}
              />
              <input
                data-testid={`history-row-label-${i}`}
                value={row.label || ""}
                onChange={(e) => setRow("history", i, "label", e.target.value)}
                placeholder="英字ラベル（例：EXPANSION）"
                className={`sm:w-44 ${inputCls}`}
              />
              <input
                data-testid={`history-row-text-${i}`}
                value={row.text}
                onChange={(e) => setRow("history", i, "text", e.target.value)}
                placeholder="出来事"
                className={`flex-1 ${inputCls}`}
              />
              <button
                data-testid={`history-row-delete-${i}`}
                onClick={() => removeRow("history", i)}
                className="self-end border border-line p-2.5 text-gray-500 transition-colors hover:border-red-500 hover:text-red-400 sm:self-auto"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          {profile.history.length === 0 && <p className="text-sm text-gray-500">沿革がありません。「年を追加」から追加してください。</p>}
        </div>
      </section>

      <section className="mt-8 border border-line bg-panel p-6 md:p-8">
        <h2 className="font-mincho text-lg font-bold text-white">変更履歴（最新20件）</h2>
        <div className="mt-4 space-y-2" data-testid="company-change-history">
          {changeLog.length === 0 && <p className="text-sm text-gray-500">まだ変更履歴はありません。</p>}
          {changeLog.map((h) => (
            <div key={h.id} className="flex items-center justify-between gap-4 border-b border-line pb-2 text-xs last:border-b-0">
              <span className="font-mono text-gray-500">{new Date(h.at).toLocaleString("ja-JP")}</span>
              <span className="text-gray-400">{h.edited_by}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
